import {
  addToSelection,
  getReport,
  getReportFiles,
  removeFromSelection,
  setForCorrection
} from '@/services/report/ReportService';
import type {
  ReportForm,
  ReportDetails,
  ReportFile,
  GetReportFilesRequest,
  ReportFileWithApprovalNo
} from '@/services/report/models';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  ReportPeriodTypeLabel,
  ReportStatus,
  ReportTypePeriodStatus
} from '@/constants/reportStatus';
import { useConfigStore } from '@/stores/configStore';
import { useAlertStore } from '@/stores/alertStore';
import { RequestStatus } from '@/core/api/types';
import { FormTypeId } from '@/components/report-forms/constants/formTypeId';
import { removeJsonSchemaFromCache } from '@/json-validation/useAjv';
import { JsonSchemaId } from '@/json-validation/constants/jsonSchemaId';
import type { ReportNotesState } from '@/components/report-notes/models/reportNotesState';
import { SortDirection } from '@/constants/sortDirection';
import { FormKind } from '@/components/report-forms/constants/formType';
import type { DynamicColumnTitleTranslationParams } from '@/components/report-forms/models/dynamicColumnTitleTranslationParams';
import { DateFormat, useDateFormat } from '@/utils/dateFormat';

export const useReportStore = defineStore(
  'report',
  () => {
    const { config } = useConfigStore();
    const { addSuccessAlert } = useAlertStore();
    const report = ref<ReportDetails | null>(null);
    const reportApprovals = ref<ReportFileWithApprovalNo[] | null>(null);
    const reportNotesState = ref<ReportNotesState>({
      note: '',
      sortDirection: SortDirection.Asc,
      isEditorAboveNotes: false
    });

    const showReportDetailsLoadingSpinner = ref(false);

    const reportName = computed(() => report.value?.name || '');

    const reportForms = computed<ReportForm[]>(
      () => report.value
        ? [...report.value.aopForms, ...report.value.jsonForms]
        : []
    );

    const selectedReportFormId = ref(0);

    const selectedReportForm = computed<ReportForm | null>(() => {
      return reportForms.value.find((f) => f.id === selectedReportFormId.value) || null;
    });

    const selectedReportFormType = computed<FormKind>(() => selectedReportForm.value?.formKind || FormKind.BUDGET_FORM);

    const selectedReportFormTypeId = computed(() => selectedReportForm.value?.formTypeId || 0);

    const isAopForm = computed(() =>
      selectedReportFormType.value === FormKind.BUDGET_FORM
      || FormTypeId.isEconomyAopForm(selectedReportFormTypeId.value)
    );

    const formPeriodEnd = computed(
      () => selectedReportForm.value?.formPeriodEnd || ''
    );
    const reportTypeName = computed(() => reportName.value?.slice(0, 5));

    const dynamicColumnTitleTranslationParams = computed<DynamicColumnTitleTranslationParams>(() => ({
      formPeriodEnd: useDateFormat(new Date(formPeriodEnd.value), DateFormat.ShortDouble),
      formPeriodEndYear: new Date(formPeriodEnd.value).getFullYear(),
      formPeriodEndPreviousYear: new Date(formPeriodEnd.value).getFullYear() - 1,
      reportTypeName: reportTypeName.value
    }));

    const selectedReportFormProgramAndProject = computed(() => {
      return {
        program: selectedReportForm.value?.program || '',
        project: selectedReportForm.value?.project || ''
      };
    });

    const reportHasMoreThanOneProject
      = computed(() => reportForms.value.filter((rf: ReportForm): boolean => rf.formTypeId === FormTypeId.FormFive).length > 1);

    const isForm5WithoutProjectAndProgram = computed(() =>
      reportForms.value.some(
        (form: ReportForm): boolean => form.formTypeId === FormTypeId.FormFive && form.program === null && form.project === null
      )
    );

    const reportsPeriodType = computed(getReportPeriodType);

    const digitalSignatureExists = computed(() => !!reportApprovals.value?.length);

    function setReport(r: ReportDetails): void {
      if (r.name !== reportName.value) {
        removeJsonSchemaFromCache(JsonSchemaId.EditReport);
      }
      report.value = r;
      resetReportNote();
    }

    function setReportFormId(id: number): void {
      selectedReportFormId.value = id;
    }

    function getReportPeriodType(): string {
      if (report.value?.status === ReportTypePeriodStatus.LOCKED) {
        return ReportPeriodTypeLabel.Historic;
      }
      if (
        report.value?.status === ReportTypePeriodStatus.ACTIVE
        || report.value?.status === ReportTypePeriodStatus.INACTIVE
      ) {
        return ReportPeriodTypeLabel.Active;
      }
      return ReportPeriodTypeLabel.Active;
    }

    async function addReportsToSelection(addReportIds: number[]) {
      const result = await addToSelection(
        addReportIds,
        getReportPeriodType() as 'active' | 'historic'
      );
      if (result.status === RequestStatus.ERROR) {
        return;
      }
      if (report.value && addReportIds.includes(report.value?.id)) {
        setReport({ ...report.value, selected: true });
      }
      return result;
    }

    async function removeReportsFromSelection(removeReportIds: number[]) {
      const result = await removeFromSelection(
        removeReportIds,
        getReportPeriodType() as 'active' | 'historic'
      );
      if (result.status === RequestStatus.ERROR) {
        return;
      }
      if (report.value && removeReportIds.includes(report.value?.id)) {
        setReport({ ...report.value, selected: false });
      }
      return result;
    }

    async function setReportForCorrection(reportName: string): Promise<RequestStatus> {
      const response = await setForCorrection(reportName);
      if (response.status === RequestStatus.ERROR) {
        return RequestStatus.ERROR;
      }
      if (report.value) {
        setReport({ ...report.value, status: ReportStatus.CORRECTION });
      }
      addSuccessAlert('reportCorrection.reportCorrected');
      return response.status;
    }

    async function fetchAndSetReport(reportName: string, setFormId = true): Promise<void> {
      showReportDetailsLoadingSpinner.value = true;
      const report = await getReport(reportName);
      showReportDetailsLoadingSpinner.value = false;
      if (report.status === RequestStatus.ERROR) {
        return;
      }
      setReport(report.data);
      if (setFormId) {
        setReportFormId(reportForms.value[0].id);
        reportApprovals.value = null;
        getReportApprovalFiles(reportName);
      }
    }

    function patchReport(data: Partial<ReportDetails>): void {
      if (!report.value) {
        return;
      }
      report.value = { ...report.value, ...data };
    }

    function patchReportJsonForm(data: ReportForm): void {
      if (!report.value) {
        return;
      }
      report.value = {
        ...report.value,
        jsonForms: report.value.jsonForms.map((form) => form.id === data.id ? data : form)
      };
    }

    function refetchReportApprovals(reportName: string): void {
      reportApprovals.value = null;
      getReportApprovalFiles(reportName);
    }

    async function getReportApprovalFiles(reportName: string): Promise<void> {
      if (reportApprovals.value) {
        return;
      }
      const response = await getReportFiles(reportName, {
        fileType: 'digitalSignature',
        perPage: config.maxAllowedRowsForDownloads
      } as GetReportFilesRequest);
      const approvals = response.files.map(
        (f: ReportFile, i: number): ReportFileWithApprovalNo => ({
          ...f,
          reportApprovalNo: i + 1
        })
      );

      reportApprovals.value = [...approvals].sort(
        (a: ReportFileWithApprovalNo, b: ReportFileWithApprovalNo) =>
          new Date(a.uploadedDate).getTime() > new Date(b.uploadedDate).getTime() ? -1 : 1
      );
    }

    function clearData() {
      report.value = null;
      reportApprovals.value = null;
      selectedReportFormId.value = 0;
      resetReportNote();
    }

    function updateReportNotesState(state: Partial<ReportNotesState>): void {
      reportNotesState.value = { ...reportNotesState.value, ...state };
    }

    function resetReportNote(): void {
      updateReportNotesState({
        note: ''
      });
    }

    return {
      report,
      reportApprovals,
      reportsPeriodType,
      reportForms,
      reportName,
      reportHasMoreThanOneProject,
      isForm5WithoutProjectAndProgram,
      selectedReportForm,
      selectedReportFormId,
      selectedReportFormTypeId,
      selectedReportFormType,
      isAopForm,
      fetchAndSetReport,
      setReport,
      addReportsToSelection,
      removeReportsFromSelection,
      setReportForCorrection,
      getReportApprovalFiles,
      refetchReportApprovals,
      clearData,
      setReportFormId,
      selectedReportFormProgramAndProject,
      digitalSignatureExists,
      reportNotesState,
      updateReportNotesState,
      resetReportNote,
      patchReport,
      patchReportJsonForm,
      dynamicColumnTitleTranslationParams,
      showReportDetailsLoadingSpinner
    };
  },
  {
    persist: {
      pick: [
        'report',
        'reportApprovals',
        'reportNotesState',
        'selectedReportFormId'
      ]
    }
  }
);
